/**
 * Editor Utilities
 * Core helper functions for Slate.js editor operations
 */
import { Editor, Node, Element as SlateElement, Transforms } from 'slate';
import { LIST_TYPES, TEXT_ALIGN_TYPES } from './constants';

// All list types including checklist
const ALL_LIST_TYPES = [...LIST_TYPES, 'checklist'];
const LIST_ITEM_TYPES = ['list-item', 'checklist-item'];
const CHECKLIST_TYPE = 'checklist';

// ============================================================================
// Block/Mark Detection
// ============================================================================

/**
 * Check if a block format is currently active
 */
export const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) {
    return false;
  }

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
    })
  );

  return Boolean(match);
};

/**
 * Check if a mark format is currently active
 */
export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks?.[format] === true;
};

/**
 * Check if cursor is inside any list block
 */
export const isInListBlock = (editor) => {
  const [match] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && ALL_LIST_TYPES.includes(n.type),
  });
  return Boolean(match);
};

/**
 * Get the current list item node and path
 */
export const getCurrentListItem = (editor) => {
  const [match] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_ITEM_TYPES.includes(n.type),
  });
  return match;
};

/**
 * Check if a list item node is empty
 */
export const isListItemEmpty = (node) => Node.string(node).length === 0;

// ============================================================================
// Block/Mark Toggle Functions
// ============================================================================

/**
 * Toggle a block format (headings, lists, quotes, alignment)
 */
export const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );
  const isList = LIST_TYPES.includes(format);
  const isChecklist = format === CHECKLIST_TYPE;

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      ALL_LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  let newProperties = {};

  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = { align: isActive ? undefined : format };
  } else if (isActive) {
    newProperties = { type: 'paragraph' };
  } else if (isList) {
    newProperties = { type: 'list-item' };
  } else if (isChecklist) {
    newProperties = { type: 'checklist-item' };
  } else {
    newProperties = { type: format };
  }

  Transforms.setNodes(editor, newProperties);

  if (!isActive && (isList || isChecklist)) {
    Transforms.wrapNodes(editor, { type: format, children: [] });
  }
};

/**
 * Toggle a mark format (bold, italic, underline, strikethrough)
 */
export const toggleMark = (editor, format) => {
  if (isMarkActive(editor, format)) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// ============================================================================
// Color Utilities
// ============================================================================

/**
 * Set text color on selection
 */
export const setTextColor = (editor, color) => {
  if (color === null) {
    Editor.removeMark(editor, 'color');
  } else {
    Editor.addMark(editor, 'color', color);
  }
};

/**
 * Set highlight/background color on selection
 */
export const setHighlightColor = (editor, color) => {
  if (color === null) {
    Editor.removeMark(editor, 'highlight');
  } else {
    Editor.addMark(editor, 'highlight', color);
  }
};

/**
 * Get current text color from selection
 */
export const getTextColor = (editor) => {
  const marks = Editor.marks(editor);
  return marks?.color || null;
};

/**
 * Get current highlight color from selection
 */
export const getHighlightColor = (editor) => {
  const marks = Editor.marks(editor);
  return marks?.highlight || null;
};

// ============================================================================
// Element Style Helper
// ============================================================================

/**
 * Get inline styles for an element based on its properties.
 * Uses conventional line-height similar to Google Docs/Notion.
 */
export const getElementStyle = (element) => {
  // Base style with tighter, more conventional line spacing
  const baseStyle = {
    textAlign: element.align,
    lineHeight: 1.5, // Standard readable line-height (150%)
    fontSize: '0.875rem',
    letterSpacing: '0.01rem',
    margin: 0,
    padding: 0,
  };

  switch (element.type) {
    case 'block-quote':
      return {
        ...baseStyle,
        borderLeft: '3px solid #ddd',
        paddingLeft: '12px',
        marginLeft: '0',
        color: '#666',
        fontStyle: 'italic',
      };
    case 'heading-one':
      return {
        ...baseStyle,
        fontSize: '1.5rem',
        fontWeight: 'bold',
        lineHeight: 1.3,
        marginTop: '0.5em',
        marginBottom: '0.25em',
      };
    case 'heading-two':
      return {
        ...baseStyle,
        fontSize: '1.25rem',
        fontWeight: 'bold',
        lineHeight: 1.35,
        marginTop: '0.4em',
        marginBottom: '0.2em',
      };
    case 'numbered-list':
    case 'bulleted-list':
      return {
        ...baseStyle,
        paddingLeft: '1.5em',
        margin: '0.25em 0',
        listStylePosition: 'outside',
      };
    case 'dashed-list':
      return {
        ...baseStyle,
        paddingLeft: '1.5em',
        margin: '0.25em 0',
        listStyleType: '"– "',
        listStylePosition: 'outside',
      };
    case 'checklist':
      return {
        ...baseStyle,
        listStyle: 'none',
        padding: 0,
        margin: '0.25em 0',
      };
    case 'paragraph':
    default:
      return baseStyle;
  }
};

// Re-export constants used by utilities
export { ALL_LIST_TYPES, CHECKLIST_TYPE, LIST_ITEM_TYPES };
